import { createFileRoute, Link } from "@tanstack/react-router";
import { formatDateOnly, relationshipLabel, codeLabel } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFacility } from "@/lib/facility/facility";
import { useSignatureQueues } from "@/hooks/use-signature-queues";
import { readState } from "@/lib/query/read-state";
import { IncidentSignatureQueue } from "./-components/incident-signature-queue";
import { QueueSummary, type QueueRow } from "./-components/queue-summary";

const TABS = ["incidents", "therapy", "care-plans"] as const;
type QueueTab = (typeof TABS)[number];

const isTab = (value: unknown): value is QueueTab =>
  typeof value === "string" && (TABS as readonly string[]).includes(value);

export const Route = createFileRoute("/_authenticated/signature-queue")({
  validateSearch: (search: Record<string, unknown>) => ({
    tab: isTab(search.tab) ? search.tab : undefined,
  }),
  component: SignatureQueue,
});

/** One hub for everything awaiting your signature, whichever queue it sits in. */
function SignatureQueue() {
  const { facilityId } = useFacility();
  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();

  // A kitchen or desk role reads no incident reports, so it has no incident queue to ask for.
  const {
    incidents,
    notes,
    plans,
    incidentsEligible: canReadIncidents,
    therapyEligible,
    planEligible,
  } = useSignatureQueues(facilityId);

  const incidentItems = incidents.data?.items ?? [];
  const noteRows: QueueRow[] = (notes.data ?? []).map((note) => ({
    id: note.id,
    residentId: note.residentId,
    subject: note.residentName,
    detail: `${note.discipline.toUpperCase()} ${note.title ?? codeLabel(note.noteType)}`,
    waitingSince: formatDateOnly(note.assistantSignedAt),
  }));
  const planRows: QueueRow[] = (plans.data ?? []).map((plan) => ({
    id: plan.planId,
    residentId: plan.residentId,
    subject: `${plan.residentLastName}, ${plan.residentFirstName}`,
    detail: relationshipLabel(plan.planType),
    waitingSince: formatDateOnly(plan.assessmentDate),
  }));

  const available: { value: QueueTab; label: string; count: number }[] = [];
  if (incidents.data?.eligible) {
    available.push({ value: "incidents", label: "Incident reports", count: incidentItems.length });
  }
  if (therapyEligible) {
    available.push({ value: "therapy", label: "Therapy notes", count: noteRows.length });
  }
  if (planEligible) {
    available.push({ value: "care-plans", label: "Care plans", count: planRows.length });
  }

  const [firstTab] = available;

  /* The title from the first frame, so the page does not jump when the queues arrive. */
  if (canReadIncidents && incidents.isPending) {
    return (
      <>
        <PageHeader title="Awaiting my signature" />
        <Skeleton className="h-9 w-80 max-w-full" />
        <Skeleton className="mt-6 h-64 w-full rounded-xl" />
      </>
    );
  }

  /* Without this, a failed read left no queues and said "nothing here is signed
     off by your role", which is a different and wrong answer. */
  if (canReadIncidents && incidents.isError) {
    return (
      <>
        <PageHeader title="Awaiting my signature" />
        <ReadFailed
          message="Couldn't load your signature queues."
          error={incidents.error}
          onRetry={() => void incidents.refetch()}
          retrying={incidents.isFetching}
        />
      </>
    );
  }

  if (!firstTab) {
    return (
      <>
        <PageHeader title="Awaiting my signature" />
        <EmptyState
          title="No signature queues"
          description="Nothing in this organization is signed off by your role."
        />
      </>
    );
  }

  const active =
    (tab && available.some((entry) => entry.value === tab) && tab) ||
    available.find((entry) => entry.count > 0)?.value ||
    firstTab.value;

  return (
    <>
      <PageHeader
        title="Awaiting my signature"
        meta="Co-sign requests across incident reports, therapy notes and care plans."
      />

      <Tabs
        value={active}
        onValueChange={(value) => {
          if (isTab(value)) void navigate({ search: { tab: value }, replace: true });
        }}
      >
        <TabsList>
          {available.map((entry) => (
            <TabsTrigger key={entry.value} value={entry.value}>
              {entry.label}
              {entry.count > 0 ? (
                <Badge variant="secondary" className="ml-2">
                  {entry.count}
                </Badge>
              ) : null}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="incidents" className="mt-6">
          <IncidentSignatureQueue items={incidentItems} />
        </TabsContent>

        <TabsContent value="therapy" className="mt-6">
          {readState(notes) === "failed" ? (
            <ReadFailed
              message="Couldn't load the therapy notes awaiting your co-signature."
              error={notes.error}
              onRetry={() => void notes.refetch()}
              retrying={notes.isFetching}
            />
          ) : (
            <QueueSummary
              rows={noteRows}
              pending={notes.isPending}
              emptyTitle="Nothing to co-sign"
              emptyDescription="Notes signed by an assistant you supervise appear here."
              action={
                <Button asChild>
                  <Link to="/therapy-cosign-queue">Open the co-sign queue</Link>
                </Button>
              }
            />
          )}
        </TabsContent>

        <TabsContent value="care-plans" className="mt-6">
          {readState(plans) === "failed" ? (
            <ReadFailed
              message="Couldn't load the care plans awaiting your signature."
              error={plans.error}
              onRetry={() => void plans.refetch()}
              retrying={plans.isFetching}
            />
          ) : (
            <QueueSummary
              rows={planRows}
              pending={plans.isPending}
              emptyTitle="Nothing waiting on you"
              emptyDescription="Plans that name you as a signer appear here until you have signed them."
              action={
                <Button asChild>
                  <Link to="/care-plan-signature-queue">Open the care plan queue</Link>
                </Button>
              }
            />
          )}
        </TabsContent>
      </Tabs>
    </>
  );
}
